import { images } from "../../../Assets/assetsHelper";
import "./Transactions.css";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import config from "../../../config";
import PrimaryButton from "../../../Components/Atoms/Buttons/Primary/PrimaryButton";
import { PulseLoader } from "react-spinners";
import { styled } from "@mui/material/styles";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import FormModal from "../../../Components/Molecules/FormModal/FormModal";
import useFetch from "../../../Hooks/useFetch";
import { paymentURL } from "../../../Hooks/URL";
import useSave from "../../../Hooks/useSave";

const ConfirmPayment = ({ transactions, handleClosePaymentModal }) => {
  const navigate = useNavigate();
  const [goSave, setGoSave] = useState(false);
  const [paymentData, setPaymentData] = useState(null);
  const { data, loading, error } = useFetch(paymentURL);
  const {
    saveState,
    loading: paymentLoading,
    newData,
  } = useSave({
    url: `${paymentURL}`,
    goSave,
    setGoSave,
  });

  useEffect(() => {
    if (saveState.dataRes.status == "success") {
      window.open(saveState.dataRes.data?.paymentUrl);
      handleClosePaymentModal();
    }
  }, [saveState]);

  useEffect(() => {
    if (data) {
      setPaymentData(data?.data);
    }
  }, [data]);

  const handlePayment = () => {
    setGoSave(true);
  };

  const formatAmount = (amountCents) => {
    return (amountCents / 100).toFixed(2);
  };

  return (
    <Box sx={{ p: 2 }}>
      {loading ? (
        <div className="loadingBox">
          <PulseLoader color={"var(--primary)"} />
        </div>
      ) : (
        <>
          <Typography
            variant="body1"
            sx={{
              mt: 0,
              textAlign: "center",
              color: "text.secondary",
            }}
          >
            Payment Summary
          </Typography>

          <Box sx={{ mt: 3, mb: 4 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mb: 2,
                borderBottom: "1px solid #e0e0e0",
                pb: 1,
              }}
            >
              <Typography variant="body1" color="text.secondary">
                Net Amount:
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {formatAmount(paymentData?.net?.amountCents)}{" "}
                {paymentData?.net?.currencyCode}
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mb: 0,
                borderBottom: "1px solid #e0e0e0",
                pb: 1,
              }}
            >
              <Typography variant="body1" color="text.secondary">
                Tax:
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {formatAmount(paymentData?.tax?.amountCents)}{" "}
                {paymentData?.tax?.currencyCode}
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mt: 0,
                pt: 2,
                borderTop: "2px solid #e0e0e0",
              }}
            >
              <Typography variant="h6" fontWeight="bold">
                Total Amount:
              </Typography>
              <Typography
                variant="h6"
                fontWeight="bold"
                color={config.PRIMARY_COLOR}
              >
                {formatAmount(paymentData?.total?.amountCents)}{" "}
                {paymentData?.total?.currencyCode}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: 2,
              mt: 2,
            }}
          >
            <Button
              onClick={handleClosePaymentModal}
              variant="outlined"
              sx={{
                minWidth: "120px",
                borderColor: config.PRIMARY_COLOR,
                color: config.PRIMARY_COLOR,
                "&:hover": {
                  borderColor: config.PRIMARY_COLOR,
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handlePayment}
              variant="contained"
              sx={{
                minWidth: "120px",
                backgroundColor: config.PRIMARY_COLOR,
                color: config.SECONDARY_COLOR,
                "&:hover": {
                  backgroundColor: config.PRIMARY_COLOR,
                  opacity: 0.9,
                },
              }}
            >
              Confirm Payment
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  "&:first-child": {
    borderRadius: "5px 0 0 0",
  },
  "&:last-child": {
    borderRadius: "0 5px 0 0",
  },
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: config.PRIMARY_COLOR,
    color: theme.palette.common.white,
    fontSize: "15px",
  },
}));

const columns = [
  { id: "code", label: "Transaction Code", minWidth: 120 },
  {
    id: "createdAt",
    label: "Date",
    minWidth: 150,
  },
  {
    id: "paymentMethod",
    label: "Payment Method",
    minWidth: 120,
  },
  {
    id: "netAmountCents",
    label: "Net Amount",
    minWidth: 120,
  },
  {
    id: "totalAmountCents",
    label: "Total Amount",
    minWidth: 120,
  },
  {
    id: "currencyCode",
    label: "Currency",
    minWidth: 100,
  },
];

function Transactions({ transactions, loading }) {
  const navigate = useNavigate();
  const [openPaymentModal, setOpenPaymentModal] = useState(false);
  const handleOpenPaymentModal = () => {
    setOpenPaymentModal(true);
  };

  const handleClosePaymentModal = () => {
    setOpenPaymentModal(false);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div className="mainContainer">
      <div className="TransactionsCont">
        {transactions?.details?.amountToPay?.amountCents > 0 &&
          transactions?.details?.canPay && (
            <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 2 }}>
              <PrimaryButton
                classBtn={"center addButton"}
                text={"Pay Now"}
                style={{
                  textTransform: "capitalize",
                  borderRadius: "5px",
                  color: config.SECONDARY_COLOR,
                  backgroundColor: config.PRIMARY_COLOR,
                  height: "40px",
                  padding: "0 30px",
                  minWidth: "100px",
                  textWrap: "noWrap",
                }}
                click={handleOpenPaymentModal}
              />
            </Box>
          )}

        <FormModal
          CustomStyle={"smallExtra"}
          Open={openPaymentModal}
          HandleClose={handleClosePaymentModal}
          Form={
            <ConfirmPayment
              transactions={transactions}
              handleClosePaymentModal={handleClosePaymentModal}
            />
          }
          Title={"Payment Confirmation"}
        />

        {!loading && transactions && (
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={6} lg={3}>
              <Card sx={{ height: "100%" }}>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Account Price
                  </Typography>
                  {transactions?.accountPrice?.discount?.percentage > 0 ? (
                    <>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ textDecoration: "line-through" }}
                      >
                        Original Price:{" "}
                        {(
                          transactions?.accountPrice?.totalPriceAmountCents /
                          100
                        ).toFixed(2)}{" "}
                        {transactions?.accountPrice?.currencyCode}
                      </Typography>
                      <Typography
                        variant="h5"
                        component="div"
                        color="success.main"
                      >
                        {(
                          transactions?.accountPrice?.discount
                            ?.totalPriceAmountCentsAfterDiscount / 100
                        ).toFixed(2)}{" "}
                        {transactions?.accountPrice?.currencyCode}
                      </Typography>
                      <Typography variant="body2" color="success.main">
                        {transactions?.accountPrice?.discount?.percentage}%
                        discount applied
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="h5" component="div">
                      {(
                        transactions?.accountPrice?.totalPriceAmountCents / 100
                      ).toFixed(2)}{" "}
                      {transactions?.accountPrice?.currencyCode}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <Card sx={{ height: "100%" }}>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Total Net Transactions
                  </Typography>
                  <Typography variant="h5" component="div">
                    {(
                      transactions?.totalTransactionsNetAmount?.amountCents /
                      100
                    ).toFixed(2)}{" "}
                    {transactions?.totalTransactionsNetAmount?.currencyCode}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total:{" "}
                    {(
                      transactions?.totalTransactionsTotalAmount?.amountCents /
                      100
                    ).toFixed(2)}{" "}
                    {transactions?.totalTransactionsTotalAmount?.currencyCode}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <Card sx={{ height: "100%" }}>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Amount to Pay
                  </Typography>
                  <Typography variant="h5" component="div">
                    {(
                      transactions?.details?.amountToPay?.amountCents / 100
                    ).toFixed(2)}{" "}
                    {transactions?.details?.amountToPay?.currencyCode}
                  </Typography>
                  <Typography
                    variant="body2"
                    color={
                      transactions?.details?.paid
                        ? "success.main"
                        : "error.main"
                    }
                  >
                    {transactions?.details?.paid ? "Paid" : "Pending"}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            {transactions?.details?.amountToRefund?.amountCents > 0 && (
              <Grid item xs={12} md={6} lg={3}>
                <Card sx={{ height: "100%" }}>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Amount to Refund
                    </Typography>
                    <Typography variant="h5" component="div">
                      {(
                        transactions?.details?.amountToRefund?.amountCents / 100
                      ).toFixed(2)}{" "}
                      {transactions?.details?.amountToRefund?.currencyCode}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        )}

        <Paper
          className="tableCont"
          sx={{ width: "fit-content", minWidth: "100%" }}
        >
          {!loading ? (
            <>
              <TableContainer>
                <Table stickyHeader aria-label="sticky table">
                  <TableHead>
                    <TableRow>
                      {columns.map((column) => (
                        <StyledTableCell
                          key={column.id}
                          align={column.align}
                          style={{
                            width: column.width,
                            minWidth: column.minWidth,
                            maxWidth: column.maxWidth,
                          }}
                        >
                          {column.label}
                        </StyledTableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {transactions?.paymentTransactions?.map((transaction) => (
                      <TableRow
                        hover
                        role="checkbox"
                        tabIndex={-1}
                        key={transaction?.code}
                      >
                        <TableCell>{transaction.code}</TableCell>
                        <TableCell>
                          {formatDate(transaction.createdAt)}
                        </TableCell>
                        <TableCell>{transaction.paymentMethod}</TableCell>
                        <TableCell>
                          {(transaction.netAmountCents / 100).toFixed(2)}{" "}
                          {transaction.currencyCode}
                        </TableCell>
                        <TableCell>
                          {(transaction.totalAmountCents / 100).toFixed(2)}{" "}
                          {transaction.currencyCode}
                        </TableCell>
                        <TableCell>{transaction.currencyCode}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {!transactions?.paymentTransactions?.length ? (
                  <h3 style={{ textAlign: "center", margin: "50px" }}>
                    No Transactions Found
                  </h3>
                ) : (
                  ""
                )}
              </TableContainer>
            </>
          ) : (
            <div className="loadingBox">
              <PulseLoader color={config.PRIMARY_COLOR} />
            </div>
          )}
        </Paper>
      </div>
    </div>
  );
}

export default Transactions;
